import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import { EnvironmentService } from "../service/Environment.service";
import { DisplayMessage, MessageResponse, RunFunctionCall, RunStatusResponse, SendMessageResponse, SubmitToolCallRequest } from "../model/models";
import { ErrorHandler } from "../service/ErrorHandler.service";
import MessageBox from "sap/m/MessageBox";
import { Button$PressEvent } from "sap/m/Button";
import TextArea, { TextArea$LiveChangeEvent } from "sap/m/TextArea";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import { SettingsService } from "../service/Settings.service";
import Log from "sap/base/Log";
import {
  Recognizer,
  SessionEventArgs,
  SpeechRecognitionCanceledEventArgs,
  SpeechRecognitionEventArgs,
  SpeechRecognizer,
} from "../libs/microsoft.cognitive.speech.sdk/microsoft.cognitiveservices.speech.sdk.bundle";
import Dialog from "sap/m/Dialog";
import SpeechService from "../service/Speech.service";
import MarkdownService from "../service/Markdown.service";
import { ListBase$UpdateFinishedEvent } from "sap/m/ListBase";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class Chat extends BaseController {
  private _oModel: JSONModel;
  private _threadId: string;
  private _runId: string;
  private _settingsService: SettingsService;
  private _chatbox: TextArea;
  private _speechService: SpeechService;
  private _logger = Log.getLogger("com.unitrade.crm.speech.Chat");
  private _currentRecognition: { ongoing: string; final: string };
  private _recognizer: SpeechRecognizer;
  private _silenceTimeoutHandle: number;
  private _audioContext: AudioContext;
  private _bufferSource: AudioBufferSourceNode;

  private static thinkMsg = "denke nach ...";
  private static waitingMsg = "wartet auf Ihre Nachricht...";
  private static preparingMsg = "bereite mich vor ...";
  private static listeningMsg = "höre zu ...";

  public async getTitle(): Promise<string> {
    return "Chat mit Uta";
  }

  private _localFunctions = ["show_customer", "create_visitreport_for_customer", "create_task"];

  onInit(): void {
    this._settingsService = new SettingsService();
    this._speechService = new SpeechService();
    this._audioContext = new AudioContext();

    this._oModel = new JSONModel({
      messages: [],
      input: "",
      inputMode: "text",
      inputEnabled: true,
      buttonMode: "speechtotext",
      utaAvatarSize: "S",
      utaVoice: "de-DE-SeraphinaMultilingualNeural",
      utaVoiceSpeed: "0",
      utaVoiceEnabled: "wheninputspeech",
      utaActionMessage: Chat.waitingMsg,
      utaSpeaking: false,
    });
    this.setModel(this._oModel);

    this.getRouter().getRoute("chat").attachPatternMatched(this._onPatternMatch, this);
  }

  onAfterRendering(): void {
    this._chatbox = this.byId("chatbox") as TextArea;
    this._chatbox.addEventDelegate({
      onkeydown: (e: jQuery.Event & { target: HTMLInputElement }) => {
        if (e.key === "Enter" && !e.shiftKey) {
          this._oModel.setProperty("/inputMode", "text");
          e.preventDefault();
          const input: string = e.target.value;
          e.target.value = "";
          this.sendMessage(input);
          this._oModel.setProperty("/buttonMode", "speechtotext");
          return false;
        }
      },
    });
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    const utaAvatarSize = this._settingsService.getSetting("uta-avatar-size");
    const utaVoice = this._settingsService.getSetting("uta-voice");
    const utaVoiceSpeed = this._settingsService.getSetting("uta-voice-speed");
    const utaVoiceEnabled = this._settingsService.getSetting("uta-voice-enabled");

    if (utaAvatarSize) {
      this._oModel.setProperty("/utaAvatarSize", utaAvatarSize);
    }

    if (utaVoiceSpeed) {
      this._oModel.setProperty("/utaVoiceSpeed", utaVoiceSpeed);
    }

    if (utaVoiceEnabled) {
      this._oModel.setProperty("/utaVoiceEnabled", utaVoiceEnabled);
    }

    if (utaVoice) {
      this._oModel.setProperty("/utaVoice", utaVoice);
    }
  };

  onChatButtonPress = async (oEvent: Button$PressEvent) => {
    const input: string = this._oModel.getProperty("/input");

    if (input && input.length > 0) {
      this._oModel.setProperty("/input", "");
      this._oModel.setProperty("/inputMode", "text");
      await this.sendMessage(input);
      this._oModel.setProperty("/buttonMode", "speechtotext");
    } else {
      this._oModel.setProperty("/inputMode", "speech");
      this.showUtaListeningDialog();
    }
  };

  showUtaListeningDialog = async () => {
    const dialog = (await this.getFragment("UtaListeningDialog", { addDependant: true })) as Dialog;
    this._oModel.setProperty("/utaActionMessage", Chat.preparingMsg);
    dialog.open();
    await this.startListening();
  };

  closeUtaListeningDialog = async () => {
    const dialog = (await this.getFragment("UtaListeningDialog")) as Dialog;
    dialog.close();
    this.stopListening();
  };

  onInputLiveChange = async (oEvent: TextArea$LiveChangeEvent) => {
    const input = oEvent.getParameter("value");
    if (input.length > 0) {
      this._oModel.setProperty("/buttonMode", "send");
    } else {
      this._oModel.setProperty("/buttonMode", "speechtotext");
    }
  };

  onListUpdateFinished = async (oEvent: ListBase$UpdateFinishedEvent) => {
    this.scrollToEnd();
  };

  private checkRunStatus = async () => {
    try {
      const status: RunStatusResponse = await $.get(`${EnvironmentService.getBaseUrl()}/assistant/status/${this._threadId}/${this._runId}`);
      const sStatus = status.status.toLowerCase();
      if (sStatus === "in_progress" || sStatus === "queued") {
        this._oModel.setProperty("/utaActionMessage", Chat.thinkMsg);
        setTimeout(this.checkRunStatus, 1500);
      } else if (sStatus === "completed") {
        this._runId = null;
        await this.loadMessages();
      } else if (sStatus === "requires_action") {
        this._oModel.setProperty("/utaActionMessage", "Lade Daten aus Unitrade ...");
        const functionCalls = status.functionCalls;
        const stcr: SubmitToolCallRequest = {
          functionResults: [],
        };
        for (const f of functionCalls) {
          if (this._localFunctions.includes(f.functionName)) {
            try {
              await this.handleFunctionOnClient(f);
              stcr.functionResults.push({ toolCallId: f.toolCallId, result: "Success" });
            } catch {
              stcr.functionResults.push({ toolCallId: f.toolCallId, result: "Error" });
            }
          } else {
            const hasArgs = Object.keys(JSON.parse(f.arguments)).length > 0;
            const result = await $.post(`${EnvironmentService.getBaseUrl()}/assistant/function/${f.functionName}`, hasArgs ? f.arguments : undefined);
            stcr.functionResults.push({ toolCallId: f.toolCallId, result: JSON.stringify(result) });
          }
        }
        await $.post(`${EnvironmentService.getBaseUrl()}/assistant/function/submitToolCall/${this._threadId}/${this._runId}`, JSON.stringify(stcr));
        this._oModel.setProperty("/utaActionMessage", Chat.thinkMsg);
        setTimeout(this.checkRunStatus, 1500);
      } else {
        throw Error("Status was " + status.status);
      }
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { onRetry: this.checkRunStatus });
    }
  };

  private handleFunctionOnClient = async (f: RunFunctionCall) => {
    switch (f.functionName) {
      case this._localFunctions[0]: {
        this.handleShowCustomer(JSON.parse(f.arguments).id);
        break;
      }
      case this._localFunctions[1]: {
        this.handleCreateVisitReportForCustomer(JSON.parse(f.arguments).id);
        break;
      }
      case this._localFunctions[2]: {
        const taskParams = JSON.parse(f.arguments);
        this.handleCreateTask(taskParams.title, taskParams.description, taskParams.assigneeId, taskParams.dueDate);
        break;
      }
    }
  };

  private handleShowCustomer = (id: string) => {
    const oIds = this.parseCustomerId(id);
    this.getRouter().navTo("customersDetails", { idPath: oIds.id, lfdPath: oIds.lfd });
  };

  private handleCreateVisitReportForCustomer = (id: string) => {
    const oIds = this.parseCustomerId(id);
    this.getRouter().navTo("visitReportForCustomer", { kundid: oIds.id, kundlfd: oIds.lfd });
  };

  private handleCreateTask(title: string, description: string, assigneeId: string, dueDate: string) {
    const queryParams: { [k: string]: any } = {};
    if (title) {
      queryParams.betreff = title;
    }

    if (description) {
      queryParams.beschreibung = description;
    }

    if (assigneeId) {
      queryParams.verantwortlich = assigneeId;
    }

    if (dueDate) {
      queryParams.faelligkeit = dueDate;
    }

    this.getRouter().navTo("taskDetails", {
      "?query": queryParams,
    });
  }

  private static idFormatError = "Id is invalid. Format should be 1 or 2 numbers (seperated by any non-numeric char. E.g.: 3242363-1";
  private parseCustomerId = (sId: string): { id: number; lfd: number } => {
    const aIds = sId.trim().split("[^0-9.]");
    if (aIds.length === 0 || aIds.length > 2) {
      throw new Error(Chat.idFormatError);
    } else {
      const id: number = parseInt(aIds[0]);
      let lfd: number = 1;
      if (aIds.length == 2) {
        lfd = parseInt(aIds[1]);
      }

      if (!(Number.isNaN(id) && Number.isNaN(lfd))) {
        return { id: id, lfd: lfd };
      } else {
        throw new Error(Chat.idFormatError);
      }
    }
  };

  private loadMessages = async () => {
    try {
      if (this._threadId) {
        const responseMessages: MessageResponse[] = await $.get(`${EnvironmentService.getBaseUrl()}/assistant/messages/${this._threadId}`);
        const messages = responseMessages.map(this._toDisplayMessage).reverse();
        messages.shift();
        this._oModel.setProperty("/messages", messages);
        this._speakText(messages[messages.length - 1].text);
      }
    } catch (ex: any) {
      ErrorHandler.handleError("Fehler beim Laden der Nachrichten", { onRetry: this.loadMessages, ignorable: true });
    }
  };

  private sendMessage = async (text: string) => {
    this._oModel.setProperty("/inputEnabled", false);
    const messages: DisplayMessage[] = this._oModel.getProperty("/messages");

    if (text && text.length > 0) {
      messages.push({
        createdAt: "",
        id: "temporaryUserMessage",
        sender: "user",
        text: text,
        isLoading: false,
        visible: true,
      });
    }

    messages.push({
      createdAt: "",
      id: "loadingMessage",
      sender: "assistant",
      text: "...",
      isLoading: true,
      visible: true,
    });

    this._oModel.setProperty("/messages", messages);

    try {
      this._oModel.setProperty("/utaActionMessage", Chat.thinkMsg);
      const sendMessageResponse: SendMessageResponse = await $.post(
        `${EnvironmentService.getBaseUrl()}/assistant/sendMessage`,
        JSON.stringify({ threadId: this._threadId, text: text })
      );

      this._threadId = sendMessageResponse.threadId;
      this._runId = sendMessageResponse.runId;
      setTimeout(this.checkRunStatus, 3000);
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { ignorable: true });

      await this.loadMessages();

      // clean up temporary messages if necessary
      if (messages[messages.length - 1].id === "loadingMessage") {
        messages.pop();
        this._oModel.setProperty("/messages", messages);
      }
      if (messages[messages.length - 1].id === "temporaryUserMessage") {
        messages.pop();
        this._oModel.setProperty("/input", text);
        this._oModel.setProperty("/buttonMode", "send");
        this._oModel.setProperty("/messages", messages);
        this._oModel.setProperty("/utaActionMessage", Chat.waitingMsg);
      }
    } finally {
      this._oModel.setProperty("/inputEnabled", true);
    }
  };

  private _toDisplayMessage(m: MessageResponse): DisplayMessage {
    return {
      createdAt: m.createdAt,
      id: m.id,
      sender: m.role,
      text: m.text,
      isLoading: false,
      visible: false,
    };
  }

  private _speakText = async (text: string): Promise<void> => {
    const utaVoiceEnabled = this._oModel.getProperty("/utaVoiceEnabled");
    const inputMode = this._oModel.getProperty("/inputMode");
    if (utaVoiceEnabled === "disabled" || (utaVoiceEnabled === "wheninputspeech" && inputMode === "text")) {
      this._oModel.setProperty("/utaActionMessage", Chat.waitingMsg);
      return;
    }

    await this._speakWithAzure(text);
  };

  private _speakWithAzure = async (text: string) => {
    this._oModel.setProperty("/utaActionMessage", "spreche gerade ...");
    this._oModel.setProperty("/utaSpeaking", true);
    const utaVoice = this._oModel.getProperty("/utaVoice");
    const utaVoiceSpeed = this._oModel.getProperty("/utaVoiceSpeed");
    let textToSpeak = MarkdownService.removeMarkdown(text);
    let synthesizer = await this._speechService.getSpeechSynthesizer();

    textToSpeak = textToSpeak
      .replaceAll("Unitrade", `<lang xml:lang="en-US">Unitrade</lang>`)
      .replaceAll("ERP", `E R P`)
      .replaceAll("PLZ", "Postleitzahl")
      .replaceAll("Lukic", "Lukitsch");

    const ssml = `
    <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="de-DE">
      <voice name="${utaVoice}">
        <prosody rate="${utaVoiceSpeed}%">
          <lang xml:lang="de-DE">
            ${textToSpeak}
          </lang>
        </prosody>
      </voice>
    </speak>
    `;

    synthesizer.speakSsmlAsync(
      ssml,
      (result) => {
        this._logger.info("Speech synthesize succeeds");

        this._bufferSource = this._audioContext.createBufferSource();
        this._audioContext.decodeAudioData(result.audioData, (buffer) => {
          this._bufferSource.buffer = buffer;
          this._bufferSource.connect(this._audioContext.destination);
          this._bufferSource.start(0);
          this._bufferSource.onended = (ev: Event) => {
            this._oModel.setProperty("/utaActionMessage", Chat.waitingMsg);
            this._oModel.setProperty("/utaSpeaking", false);
          };
        });

        synthesizer.close();
        synthesizer = undefined;
      },
      (err) => {
        this._logger.error("Speech synthesize failed: " + err);
        synthesizer.close();
        synthesizer = undefined;
      }
    );
  };

  utaStopSpeaking = async (oEvent: Button$PressEvent) => {
    this._bufferSource.stop();

    this._oModel.setProperty("/utaActionMessage", Chat.waitingMsg);
    this._oModel.setProperty("/utaSpeaking", false);
  };

  private startListening = async () => {
    try {
      const recognizer = await this.getSpeechRecognizer();
      await recognizer.startContinuousRecognitionAsync();
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { ignorable: true });
      this._oModel.setProperty("/utaActionMessage", Chat.waitingMsg);
    }
  };

  private stopListening = async () => {
    const recognizer = await this.getSpeechRecognizer();
    await recognizer.stopContinuousRecognitionAsync();
  };

  private onRecognizing = (sender: Recognizer, event: SpeechRecognitionEventArgs) => {
    if (event.result.text) {
      this._currentRecognition.ongoing = event.result.text;
      this._oModel.setProperty("/input", this._currentRecognition.final + this._currentRecognition.ongoing + "[...]");
      this.startOrResetSilencetimer();
    }
  };

  private onCanceled = (sender: Recognizer, event: SpeechRecognitionCanceledEventArgs) => {
    MessageBox.warning("Azure Spracherkennung abgebrochen", { details: event.errorDetails });
    this._recognizer = null;
  };

  private onRecognized = (sender: Recognizer, event: SpeechRecognitionEventArgs) => {
    if (event.result.text) {
      this._currentRecognition.ongoing = "";
      this._currentRecognition.final += event.result.text + " ";
      this._oModel.setProperty("/input", this._currentRecognition.final + this._currentRecognition.ongoing + "[...]");
      this.startOrResetSilencetimer();
    }
  };

  private onSessionStarted = (sender: Recognizer, event: SessionEventArgs) => {
    this._oModel.setProperty("/utaActionMessage", Chat.listeningMsg);
    const current = this._oModel.getProperty("/input");
    console.log(current);
    this._currentRecognition = {
      ongoing: "",
      final: current,
    };
    this._oModel.setProperty("/input", this._currentRecognition.final + "[...]");
  };

  private onSessionStopped = async (sender: Recognizer, event: SessionEventArgs) => {
    await this.sendMessage(this._currentRecognition.final);
    this._oModel.setProperty("/input", "");
    this.clearSilenceTimeout();
  };

  private startOrResetSilencetimer = () => {
    this.clearSilenceTimeout();

    this._silenceTimeoutHandle = window.setTimeout(this.closeUtaListeningDialog, 1500);
  };

  private clearSilenceTimeout = () => {
    if (this._silenceTimeoutHandle) {
      window.clearTimeout(this._silenceTimeoutHandle);
    }
    this._silenceTimeoutHandle = undefined;
  };

  private getSpeechRecognizer = async () => {
    if (!this._recognizer) {
      const recognizer = await this._speechService.getSpeechRecognizer();
      recognizer.recognizing = this.onRecognizing;
      recognizer.recognized = this.onRecognized;
      recognizer.canceled = this.onCanceled;
      recognizer.sessionStarted = this.onSessionStarted;
      recognizer.sessionStopped = this.onSessionStopped;

      this._recognizer = recognizer;
    }
    return this._recognizer;
  };

  private scrollToEnd() {
    $(".sapUxAPObjectPageWrapper").animate({ scrollTop: $(".sapUxAPObjectPageWrapper").prop("scrollHeight") }, 1000);
  }
}
